import {
  useWSModalCustom,
  useWSSnackbar,
  WSActions,
  WSAlert,
  WSForm,
  WSList,
  WSModal,
  WSModalCustomProps,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayeeEngagementType, WorkLog } from "@wingspanhq/wingspan-api-sdk";
import { queriesWorkLogs } from "@query";
import { useQueryPayPeriodPayrollRun } from "../../hooks/useQueryPayPeriodPayrollRun";
import { selectorWorkLogApprovalWindow } from "../../selectors/selectorWorkLogApprovalWindow";
import { selectorWorkLogConvertedResourceLabels } from "../../selectors/selectorWorkLogConvertedResourceLabels";

type Props = {
  workLog: WorkLog;
  /**
   * Names what approving will produce. The work log has not converted yet, so
   * `convertedResourceType` is null here and the engagement is the only signal.
   */
  engagementType?: PayeeEngagementType | null;
} & WSModalCustomProps<{
  success?: boolean;
}>;

export const ModalApproveWorkLog: React.FC<Props> = ({
  workLog,
  engagementType,
  ...modalProps
}) => {
  const { openSnackbar } = useWSSnackbar();
  // A W-2 work log converts into a pay statement, a 1099 one into an invoice.
  const convertedResourceNoun = selectorWorkLogConvertedResourceLabels({
    convertedResourceType: workLog.convertedResourceType,
    engagementType
  }).noun;

  // Which payroll run this approval would join, and whether its approval window
  // is still open.
  const { payrollRun, isLoading: isPayrollRunLoading } =
    useQueryPayPeriodPayrollRun(workLog.payPeriodId);
  const approvalWindow = selectorWorkLogApprovalWindow({ payrollRun });
  const [approveWorkLog, approveMeta] =
    queriesWorkLogs.approveWorkLog.useMutation();

  const handleApprove = async () => {
    approveWorkLog(
      {
        workLogId: workLog.id!
      },
      {
        onSuccess: () => {
          modalProps.onClose({ success: true });
          openSnackbar({
            icon: { name: "check" },
            message: "Work log approved successfully"
          });
        },
        onError: () => {
          openSnackbar({
            type: "warning",
            message: "Failed to approve work log. Please try again."
          });
        }
      }
    );
  };

  return (
    <WSModal title="Approve work log" {...modalProps}>
      <WSForm onSubmit={handleApprove}>
        <WSList gap="XL">
          <WSText.ParagraphSm color="gray500">
            Are you sure you want to approve the work log?
          </WSText.ParagraphSm>

          {isPayrollRunLoading ? (
            <WSAlert
              icon="info-circle"
              theme="info"
              title="About approved work logs"
            >
              Checking which payroll run this work log would join…
            </WSAlert>
          ) : (
            <WSAlert
              icon={
                approvalWindow.theme === "warning" ? "alert" : "info-circle"
              }
              theme={approvalWindow.theme}
              title={approvalWindow.title}
            >
              {approvalWindow.message}
              {"\n\n"}
              {`Note: You can still correct an unpaid ${convertedResourceNoun} and its work logs if needed. Once paid, neither can be changed.`}
            </WSAlert>
          )}

          {/* TODO(v3-blocker): the design shows a public comment field here, but
              `approveWorkLog` takes no body (`{workLogId, idempotencyKey?,
              xWingspanAccount?}`) and `WorkLogUpdate` carries only
              notes/attachments — there is nowhere to persist it. Collecting a
              comment under a "visible to both you and the contractor" promise
              and then dropping it is the same deception the payee's
              "Cancel request for editing" was removed for, so the field stays
              out until v3 can store it. */}

          <WSActions
            alignment="fill"
            buttons={[
              {
                kind: "Secondary",
                label: "No, go back",
                type: "button",
                onClick: () => {
                  modalProps.onClose({ success: false });
                }
              },
              {
                kind: "Primary",
                label: "Yes, approve work log",
                loading: approveMeta.isLoading
              }
            ]}
          />
        </WSList>
      </WSForm>
    </WSModal>
  );
};

export const useModalApproveWorkLog = () =>
  useWSModalCustom(ModalApproveWorkLog);
